import type { LocalDict } from "../../core/i18n";

// View-local translations for the Overview view. Short stable dot keys; {var}
// placeholders are interpolated by useT. Reuses shared keys (nav.*, common.*)
// via the useT fallback chain where they fit.
export const STR: LocalDict = {
  en: {
    // ── topbar + header ──
    "title": "Overview",
    "btn.manageAgents": "Manage agents",
    "btn.openChat": "Open chat",
    "subtitle": "Live snapshot of your Olano gateway",
    "quarantine.title": "Deployment quarantined",
    "quarantine.body": "This deployment has no valid license or was suspended. Only the default agent remains available for recovery. Install a valid license (olano deployment install) or restore cloud connectivity to resume full service.",
    // ── load error ──
    "err.unreachable": "Cannot reach the runtime",
    "err.unreachable.default": "Cannot reach the runtime.",
    "err.refreshAgents": "Could not refresh agents.",
    "btn.retry": "Retry",
    // ── insights teaser ──
    "insights.spend": "API cost ({n}d)",
    "insights.creditsLeft": "Credits left",
    "insights.credits": "Credits used ({n}d)",
    "insights.modelCalls": "Model calls",
    "insights.services": "Service calls",
    "insights.tools": "Tool calls",
    "insights.viewAll": "View insights",
    // ── stat cards ──
    "stat.agents": "Agents",
    "stat.agents.open": "Agents — open the agents view",
    "stat.channels": "Channels",
    "stat.scheduledTasks": "Scheduled tasks",
    "stat.status": "Status",
    "stat.status.live": "Live",
    "stat.running": "{n} running",
    "stat.idle": " · {n} idle",
    "stat.active": "{n} active",
    "stat.activeOf": "{n} of {total} active",
    "stat.noConnectors": "no connectors",
    "stat.enabled": "{n} enabled",
    "stat.noneEnabled": "none enabled",
    "stat.uptime": "uptime {value}",
    // ── system control ──
    "sys.title": "System control",
    "sys.desc.live":
      "The Olano runtime server is live. Restart reloads every agent and channel; Stop halts them all.",
    "sys.desc.stopped":
      "The Olano runtime server is stopped. Start boots the server, its agents, and channels.",
    "sys.start": "Start",
    "sys.start.busy": "Starting…",
    "sys.stop": "Stop",
    "sys.stop.busy": "Stopping…",
    "sys.restart": "Restart",
    "sys.restart.busy": "Restarting…",
    // ── system toasts ──
    "toast.systemStarted": "System Started",
    "toast.systemStopped": "System Stopped",
    "toast.systemRestarted": "System Restarted",
    "toast.systemStartFailed": "System start failed: {err}",
    "toast.systemStopFailed": "System stop failed: {err}",
    "toast.systemRestartFailed": "System restart failed: {err}",
    "err.unknown": "unknown error",
    // ── agent action toasts ──
    "toast.agentStarted": "Started {name}",
    "toast.agentStopped": "Stopped {name}",
    "toast.agentRestarted": "Restarted {name}",
    "toast.agentStartFailed": "Could not start {name}: {err}",
    "toast.agentStopFailed": "Could not stop {name}: {err}",
    "toast.agentRestartFailed": "Could not restart {name}: {err}",
    // ── agents section ──
    "agents.heading": "Agents",
    "agents.collapse": "Collapse agents",
    "agents.expand": "Expand agents",
    "agents.viewAll": "View all",
    "agents.prevPage": "Previous page",
    "agents.nextPage": "Next page",
    "agents.empty.title": "No agents yet",
    "agents.empty.desc":
      "Create your first agent to get started — it can chat, run tools, and respond on Telegram, Slack, Discord, or WhatsApp.",
    "agents.empty.create": "Create agent",
    // ── recent activity ──
    "activity.heading": "Recent activity",
    "activity.clear": "Clear",
    "activity.clear.title": "Clear recent activity from this view",
    "activity.allLogs": "All logs",
    "activity.empty": "Logs will appear here in real time.",
    // ── agent card / detail ──
    "agent.noDescription": "No description.",
    "agent.chat": "Chat",
    "agent.edit": "Edit",
    "agent.restart": "Restart",
    "agent.stop": "Stop",
    "agent.start": "Start",
    "agent.close": "Close",
    "agent.closeDetails": "Close details",
    "agent.connectedChannels": "Connected channels",
    // ── capability stat labels ──
    "cap.subagents": "Subagents",
    "cap.tools": "Tools",
    "cap.skills": "Skills",
    "cap.mcps": "MCPs",
    "cap.channels": "Channels",
    // ── badge labels (singular/plural) ──
    "badge.subagent": "{n} subagent",
    "badge.subagents": "{n} subagents",
    "badge.tool": "{n} tool",
    "badge.tools": "{n} tools",
    "badge.skill": "{n} skill",
    "badge.skills": "{n} skills",
    "badge.mcp": "{n} mcp",
    "badge.mcps": "{n} mcps",
    "badge.channel": "{n} channel",
    "badge.channels": "{n} channels",
    // ── status words ──
    "status.running": "running",
    "status.error": "error",
    "status.stopped": "stopped",
    "status.starting": "starting",
    "status.stopping": "stopping",
    "status.paused": "paused",
    "status.configured": "configured",
    // ── credits card ──
    "credits.title": "Olano credits",
    "credits.subtitle": "Pays for Olano-managed AI on this deployment",
    "credits.staleSuffix": " · showing a cached balance (cloud unreachable)",
    "credits.balanceBreakdown": "{included} included + {purchased} purchased",
    "credits.left": "left",
    "credits.monthlyIncluded": "Monthly included",
    "credits.resetsSuffix": " · resets {reset}",
    "credits.usedLabel": "Monthly included credits used",
    "credits.usedOf": "{used} / {total} used",
    "credits.purchasedTopups": "Purchased top-ups (never expire):",
    "credits.buyCredits": "Buy credits",
    // ── services usage card ──
    "services.title": "Olano credits by service",
    "services.lastDays": "Last {days} days",
    "services.staleSuffix": " · showing cached figures (cloud unreachable)",
    "services.total": "total",
    // ── disk card ──
    "disk.hint.usage":
      "How full the monitored filesystem is, measured the same way `df` reports it. The markers show the warn and auto-clean thresholds.",
    "disk.hint.autoClean":
      "When usage crosses the auto-clean threshold, the runtime runs the enabled cleanup actions (safe ones by default — caches and old logs). Configure thresholds and actions in Config → Disk monitor & cleanup.",
    "disk.hint.cleanNow":
      "Run the enabled cleanup actions immediately (docker prune can take a few minutes). The runtime keeps serving while it runs.",
    "disk.hint.lastCleanup": "What the most recent cleanup reclaimed, per action.",
    "disk.hint.actions":
      "Which cleanup actions are enabled and whether they can run on this host. Aggressive actions (docker prune) are opt-in — turn them on in Config → Disk monitor & cleanup.",
    "disk.hint.largest":
      "Where the bytes are inside the Olano home only — from the daily directory scan (throttled; two levels deep, plus two below each agent). This does not include Docker or the operating system; see Where the space went above for those. Click a directory to see what's inside it.",
    "disk.hint.scanNow":
      "Walk the olano home now instead of waiting for the daily scan (runs in the background, niced and throttled). Also re-reads the Docker and whole-disk figures above.",
    "disk.hint.breakdown":
      "Every byte of used space on this disk, grouped: the Docker images and volumes this deployment runs on, the Olano home (agents, transcripts, memory, skills), the swap file, and — as what is left over — the operating system and other host files. The Olano home is usually a small slice; the rest is the platform itself. Figures come from the container runtime and the daily home scan, so they are accurate to about a percent, not to the byte.",
    "disk.hint.breakdownSystem":
      "This group is measured as the used space minus everything else, because the runtime runs inside a container and cannot read the host's own directories. It covers the operating system and kernel, the Docker engine, system logs, package caches and the deployment checkout.",
    "disk.breakdown.title": "Where the space went · {used} used of {total}",
    "disk.breakdown.reclaimable": "{bytes} reclaimable",
    "disk.breakdown.free": "Free",
    "disk.breakdown.notMeasured": "Not measured:",
    "disk.expand": "Expand disk details",
    "disk.collapse": "Collapse disk details",
    "disk.title": "Disk",
    "disk.unavailable": "Unavailable: {err}",
    "disk.unavailableFallback": "unavailable",
    "disk.loading": "Loading…",
    "disk.word.on": "on",
    "disk.word.off": "off",
    "disk.summary.collapsed": "{free} free of {total} on {path} · auto-clean {state}",
    "disk.cleaningSuffix": " · cleaning now…",
    "disk.summary.expanded": "{path} · {free} free of {total} · checked every {interval} min",
    "disk.envDisabledSuffix": " · disabled by LIONCLAW_NO_DISK_MONITOR=1",
    "disk.autoClean.on": "Auto-clean: On",
    "disk.autoClean.off": "Auto-clean: Off",
    "disk.clean.now": "Clean now",
    "disk.clean.busy": "Cleaning…",
    "disk.stat.used": "Used",
    "disk.stat.free": "Free",
    "disk.stat.lastCleanup": "Last cleanup",
    "disk.never": "never",
    "disk.warnPct": "warn {pct}%",
    "disk.autoCleanPct": "auto-clean {pct}%",
    "disk.legend": "warn {warn}% · auto-clean {cleanup}%",
    "disk.largestDirectories": "Largest directories in the Olano home",
    "disk.scanSuffix": " · {bytes} scanned {time}",
    "disk.partialSuffix": " (partial)",
    "disk.scan.now": "Scan now",
    "disk.scan.busy": "Scanning…",
    "disk.scan.firstRunning": "First scan running…",
    "disk.scan.noneYet": "No scan yet — it runs daily, or press Scan now.",
    "disk.dirRow.title": "{files} files — click to {action}",
    "disk.action.collapse": "collapse",
    "disk.action.seeInside": "see inside",
    "disk.filesCount": "{files} files",
    "disk.cleanupActions": "Cleanup actions",
    "disk.aggressive": "aggressive",
    "disk.lastCleanupHeading": "Last cleanup · {trigger} · {before}% → {after}%",
    "disk.reasonWrapped": "({reason})",
    // ── memory card ──
    "memory.hint.rss": "Resident Set Size — the actual physical RAM the runtime is using right now.",
    "memory.hint.pss":
      "Proportional Set Size — like RSS, but memory shared with other programs is counted only in proportion. The fairest single number for what this process really costs.",
    "memory.hint.vms":
      "Virtual Memory Size — the total address space the process has reserved, including memory it isn't actively using. Normally much larger than real RAM use, and not a concern on its own.",
    "memory.hint.threads": "Number of OS threads the runtime is currently running.",
    "memory.hint.uptime": "How long the runtime has been running since it last (re)started.",
    "memory.hint.toolArgs":
      "Entries in the tool-argument registry — a capped cache of recent tool-call arguments.",
    "memory.hint.hookCtx": "Active per-conversation hook contexts held in memory.",
    "memory.hint.offsets": "Self-learning read positions tracked across the session logs.",
    "memory.hint.runners": "Agent runtimes currently loaded in memory.",
    "memory.hint.embedModel":
      "Whether the text-embedding model is loaded in RAM (used for Cortex / knowledge search).",
    "memory.hint.sampler":
      "How often the background monitor records a memory reading — “off” when the monitor is disabled.",
    "memory.hint.bootRss":
      "Approximate RAM each agent added when it booted, measured around startup. Directional, not exact.",
    "memory.hint.subprocesses":
      "RAM used by helper programs (browser, MCP connectors, signal-cli) that run as separate processes, on top of the runtime's own memory.",
    "memory.expand": "Expand memory details",
    "memory.collapse": "Collapse memory details",
    "memory.title": "Memory",
    "memory.unavailable": "Unavailable: {err}",
    "memory.unavailableFallback": "unavailable",
    "memory.loading": "Loading…",
    "memory.word.on": "on",
    "memory.word.off": "off",
    "memory.summary.monitorSuffix": " · monitor {state}",
    "memory.summary.source": "source: {source}",
    "memory.noDaemonSuffix": " · no daemon",
    "memory.monitorToggleHint":
      "Toggle the background memory sampler + tracemalloc (off frees the monitor's own memory)",
    "memory.monitor.on": "Monitor: On",
    "memory.monitor.off": "Monitor: Off",
    "memory.stat.rss": "RSS",
    "memory.stat.pss": "Pss",
    "memory.stat.vmSize": "VmSize",
    "memory.stat.threads": "Threads",
    "memory.stat.uptime": "Uptime",
    "memory.term.toolArgs": "tool_args",
    "memory.term.hookCtx": "hook_ctx",
    "memory.term.offsets": "offsets",
    "memory.term.runners": "runners",
    "memory.term.embedModel": "embed_model",
    "memory.term.sampler": "sampler",
    "memory.embedModel.resident": "resident",
    "memory.embedModel.no": "no",
    "memory.sampler.interval": "{n}s",
    "memory.tracemallocOn": " · tracemalloc on",
    "memory.perAgentHeading": "Per-agent boot Δ-RSS (approx)",
    "memory.subprocessesHeading": "Subprocesses · +{mb} (separate PIDs)",
  },
  zh: {
    "title": "概览",
    "btn.manageAgents": "管理智能体",
    "btn.openChat": "打开对话",
    "subtitle": "Olano 网关的实时快照",
    "quarantine.title": "部署已隔离",
    "quarantine.body": "此部署没有有效许可证或已被暂停。仅默认智能体可用于恢复。请安装有效许可证（olano deployment install）或恢复云端连接以恢复完整服务。",
    "err.unreachable": "无法连接到运行时",
    "err.unreachable.default": "无法连接到运行时。",
    "err.refreshAgents": "无法刷新智能体。",
    "btn.retry": "重试",
    "insights.spend": "API 费用（{n}天）",
    "insights.creditsLeft": "剩余积分",
    "insights.credits": "已用积分（{n}天）",
    "insights.modelCalls": "模型调用",
    "insights.services": "服务调用",
    "insights.tools": "工具调用",
    "insights.viewAll": "查看洞察",
    "stat.agents": "智能体",
    "stat.agents.open": "智能体 — 打开智能体页面",
    "stat.channels": "通道",
    "stat.scheduledTasks": "计划任务",
    "stat.status": "状态",
    "stat.status.live": "运行中",
    "stat.running": "{n} 个运行中",
    "stat.idle": " · {n} 个空闲",
    "stat.active": "{n} 个活跃",
    "stat.activeOf": "{total} 个中 {n} 个活跃",
    "stat.noConnectors": "无连接器",
    "stat.enabled": "{n} 个已启用",
    "stat.noneEnabled": "未启用任何任务",
    "stat.uptime": "运行时长 {value}",
    "sys.title": "系统控制",
    "sys.desc.live": "Olano 运行时服务器正在运行。重启会重新加载所有智能体和通道；停止则会全部停止。",
    "sys.desc.stopped": "Olano 运行时服务器已停止。启动会引导服务器及其智能体和通道。",
    "sys.start": "启动",
    "sys.start.busy": "启动中…",
    "sys.stop": "停止",
    "sys.stop.busy": "停止中…",
    "sys.restart": "重启",
    "sys.restart.busy": "重启中…",
    "toast.systemStarted": "系统已启动",
    "toast.systemStopped": "系统已停止",
    "toast.systemRestarted": "系统已重启",
    "toast.systemStartFailed": "系统启动失败：{err}",
    "toast.systemStopFailed": "系统停止失败：{err}",
    "toast.systemRestartFailed": "系统重启失败：{err}",
    "err.unknown": "未知错误",
    "toast.agentStarted": "已启动 {name}",
    "toast.agentStopped": "已停止 {name}",
    "toast.agentRestarted": "已重启 {name}",
    "toast.agentStartFailed": "无法启动 {name}：{err}",
    "toast.agentStopFailed": "无法停止 {name}：{err}",
    "toast.agentRestartFailed": "无法重启 {name}：{err}",
    "agents.heading": "智能体",
    "agents.collapse": "收起智能体",
    "agents.expand": "展开智能体",
    "agents.viewAll": "查看全部",
    "agents.prevPage": "上一页",
    "agents.nextPage": "下一页",
    "agents.empty.title": "暂无智能体",
    "agents.empty.desc": "创建你的第一个智能体即可开始——它可以对话、调用工具，并在 Telegram、Slack、Discord 或 WhatsApp 上回复。",
    "agents.empty.create": "创建智能体",
    "activity.heading": "最近活动",
    "activity.clear": "清除",
    "activity.clear.title": "从此视图清除最近活动",
    "activity.allLogs": "全部日志",
    "activity.empty": "日志将在此处实时显示。",
    "agent.noDescription": "暂无描述。",
    "agent.chat": "对话",
    "agent.edit": "编辑",
    "agent.restart": "重启",
    "agent.stop": "停止",
    "agent.start": "启动",
    "agent.close": "关闭",
    "agent.closeDetails": "关闭详情",
    "agent.connectedChannels": "已连接的通道",
    "cap.subagents": "子智能体",
    "cap.tools": "工具",
    "cap.skills": "技能",
    "cap.mcps": "MCP",
    "cap.channels": "通道",
    "badge.subagent": "{n} 个子智能体",
    "badge.subagents": "{n} 个子智能体",
    "badge.tool": "{n} 个工具",
    "badge.tools": "{n} 个工具",
    "badge.skill": "{n} 个技能",
    "badge.skills": "{n} 个技能",
    "badge.mcp": "{n} 个 MCP",
    "badge.mcps": "{n} 个 MCP",
    "badge.channel": "{n} 个通道",
    "badge.channels": "{n} 个通道",
    "status.running": "运行中",
    "status.error": "错误",
    "status.stopped": "已停止",
    "status.starting": "启动中",
    "status.stopping": "停止中",
    "status.paused": "已暂停",
    "status.configured": "已配置",
    "credits.title": "Olano 积分",
    "credits.subtitle": "用于支付此部署上 Olano 托管的 AI 费用",
    "credits.staleSuffix": " · 显示缓存余额（无法连接云端）",
    "credits.balanceBreakdown": "{included} 包含 + {purchased} 已购买",
    "credits.left": "剩余",
    "credits.monthlyIncluded": "每月包含额度",
    "credits.resetsSuffix": " · 于 {reset} 重置",
    "credits.usedLabel": "已使用的每月包含积分",
    "credits.usedOf": "{used} / {total} 已使用",
    "credits.purchasedTopups": "已购买充值（永不过期）：",
    "credits.buyCredits": "购买积分",
    "services.title": "按服务划分的 Olano 积分",
    "services.lastDays": "最近 {days} 天",
    "services.staleSuffix": " · 显示缓存数据（无法连接云端）",
    "services.total": "总计",
    "disk.hint.usage": "受监控文件系统的占用情况，测量方式与 `df` 相同。标记线表示警告和自动清理阈值。",
    "disk.hint.autoClean": "当占用率超过自动清理阈值时，运行时会执行已启用的清理操作（默认仅安全操作——缓存和旧日志）。可在 配置 → 磁盘监控与清理 中设置阈值和操作。",
    "disk.hint.cleanNow": "立即运行已启用的清理操作（docker prune 可能需要几分钟）。运行期间服务不会中断。",
    "disk.hint.lastCleanup": "最近一次清理按操作回收的空间。",
    "disk.hint.actions": "哪些清理操作已启用，以及它们能否在此主机上运行。激进操作（docker prune）需手动开启——在 配置 → 磁盘监控与清理 中设置。",
    "disk.hint.largest": "仅统计 Olano 主目录内的字节占用——来自每日目录扫描（节流；深两级，加上每个智能体下再深两级）。不含 Docker 和操作系统，那部分请看上方的“空间去哪了”。点击目录可查看其内部内容。",
    "disk.hint.scanNow": "立即扫描 olano 主目录，而不必等待每日扫描（在后台以低优先级、节流方式运行）。同时会重新读取上方的 Docker 与整盘数据。",
    "disk.hint.breakdown": "本磁盘已用空间的每一个字节，按组划分：该部署运行所依赖的 Docker 镜像与卷、Olano 主目录（智能体、会话记录、记忆、技能）、交换文件，以及作为剩余部分的操作系统和其他主机文件。Olano 主目录通常只占一小部分，其余是平台本身。数据来自容器运行时和每日主目录扫描，精度约为百分之一，并非精确到字节。",
    "disk.hint.breakdownSystem": "该组按“已用空间减去其他所有部分”计算，因为运行时在容器内，无法读取宿主机自身的目录。它包含操作系统与内核、Docker 引擎、系统日志、软件包缓存以及部署代码。",
    "disk.breakdown.title": "空间去哪了 · 已用 {used}，共 {total}",
    "disk.breakdown.reclaimable": "可回收 {bytes}",
    "disk.breakdown.free": "可用",
    "disk.breakdown.notMeasured": "未测量：",
    "disk.expand": "展开磁盘详情",
    "disk.collapse": "收起磁盘详情",
    "disk.title": "磁盘",
    "disk.unavailable": "不可用：{err}",
    "disk.unavailableFallback": "不可用",
    "disk.loading": "加载中…",
    "disk.word.on": "开",
    "disk.word.off": "关",
    "disk.summary.collapsed": "{free} 可用，共 {total}（{path}）· 自动清理{state}",
    "disk.cleaningSuffix": " · 正在清理…",
    "disk.summary.expanded": "{path} · {free} 可用，共 {total} · 每 {interval} 分钟检查一次",
    "disk.envDisabledSuffix": " · 已通过 LIONCLAW_NO_DISK_MONITOR=1 禁用",
    "disk.autoClean.on": "自动清理：开",
    "disk.autoClean.off": "自动清理：关",
    "disk.clean.now": "立即清理",
    "disk.clean.busy": "清理中…",
    "disk.stat.used": "已用",
    "disk.stat.free": "可用",
    "disk.stat.lastCleanup": "上次清理",
    "disk.never": "从未",
    "disk.warnPct": "警告 {pct}%",
    "disk.autoCleanPct": "自动清理 {pct}%",
    "disk.legend": "警告 {warn}% · 自动清理 {cleanup}%",
    "disk.largestDirectories": "Olano 主目录中最大的目录",
    "disk.scanSuffix": " · 已扫描 {bytes}，时间 {time}",
    "disk.partialSuffix": "（部分）",
    "disk.scan.now": "立即扫描",
    "disk.scan.busy": "扫描中…",
    "disk.scan.firstRunning": "首次扫描进行中…",
    "disk.scan.noneYet": "尚无扫描——每天自动运行，或点击“立即扫描”。",
    "disk.dirRow.title": "{files} 个文件 — 点击以{action}",
    "disk.action.collapse": "收起",
    "disk.action.seeInside": "查看内部",
    "disk.filesCount": "{files} 个文件",
    "disk.cleanupActions": "清理操作",
    "disk.aggressive": "激进",
    "disk.lastCleanupHeading": "上次清理 · {trigger} · {before}% → {after}%",
    "disk.reasonWrapped": "（{reason}）",
    "memory.hint.rss": "常驻集大小（RSS）——运行时当前实际占用的物理内存。",
    "memory.hint.pss": "按比例分配集大小（PSS）——与 RSS 类似，但与其他程序共享的内存仅按比例计入。是衡量该进程真实开销的最公平指标。",
    "memory.hint.vms": "虚拟内存大小（VMS）——进程已保留的总地址空间，包括尚未实际使用的部分。通常远大于实际内存占用，本身不必担心。",
    "memory.hint.threads": "运行时当前运行的操作系统线程数。",
    "memory.hint.uptime": "自运行时上次（重新）启动以来经过的时间。",
    "memory.hint.toolArgs": "工具参数注册表中的条目——一个有容量上限的近期工具调用参数缓存。",
    "memory.hint.hookCtx": "当前保存在内存中的活跃对话级钩子上下文。",
    "memory.hint.offsets": "在会话日志中跟踪的自学习读取位置。",
    "memory.hint.runners": "当前加载在内存中的智能体运行时数量。",
    "memory.hint.embedModel": "文本嵌入模型是否已加载到内存中（用于 Cortex / 知识库搜索）。",
    "memory.hint.sampler": "后台监控器记录一次内存读数的频率——监控器关闭时显示“off”。",
    "memory.hint.bootRss": "每个智能体启动时增加的大致内存量，在启动前后测量。仅供参考，非精确值。",
    "memory.hint.subprocesses": "辅助程序（浏览器、MCP 连接器、signal-cli）作为独立进程运行所占用的内存，叠加在运行时自身内存之上。",
    "memory.expand": "展开内存详情",
    "memory.collapse": "收起内存详情",
    "memory.title": "内存",
    "memory.unavailable": "不可用：{err}",
    "memory.unavailableFallback": "不可用",
    "memory.loading": "加载中…",
    "memory.word.on": "开",
    "memory.word.off": "关",
    "memory.summary.monitorSuffix": " · 监控{state}",
    "memory.summary.source": "来源：{source}",
    "memory.noDaemonSuffix": " · 无守护进程",
    "memory.monitorToggleHint": "切换后台内存采样器 + tracemalloc（关闭可释放监控器自身占用的内存）",
    "memory.monitor.on": "监控：开",
    "memory.monitor.off": "监控：关",
    "memory.stat.rss": "RSS",
    "memory.stat.pss": "Pss",
    "memory.stat.vmSize": "VmSize",
    "memory.stat.threads": "线程数",
    "memory.stat.uptime": "运行时长",
    "memory.term.toolArgs": "工具参数",
    "memory.term.hookCtx": "钩子上下文",
    "memory.term.offsets": "偏移量",
    "memory.term.runners": "运行器",
    "memory.term.embedModel": "嵌入模型",
    "memory.term.sampler": "采样器",
    "memory.embedModel.resident": "已加载",
    "memory.embedModel.no": "否",
    "memory.sampler.interval": "{n} 秒",
    "memory.tracemallocOn": " · tracemalloc 已开启",
    "memory.perAgentHeading": "每个智能体启动时的内存增量（近似值）",
    "memory.subprocessesHeading": "子进程 · +{mb}（独立 PID）",
  },
  es: {
    "title": "Resumen",
    "btn.manageAgents": "Gestionar agentes",
    "btn.openChat": "Abrir chat",
    "subtitle": "Vista en vivo de tu pasarela Olano",
    "quarantine.title": "Despliegue en cuarentena",
    "quarantine.body": "Este despliegue no tiene una licencia válida o fue suspendido. Solo el agente predeterminado sigue disponible para recuperación. Instala una licencia válida (olano deployment install) o restaura la conexión con la nube.",
    "err.unreachable": "No se puede conectar con el runtime",
    "err.unreachable.default": "No se puede conectar con el runtime.",
    "err.refreshAgents": "No se pudieron actualizar los agentes.",
    "btn.retry": "Reintentar",
    "insights.spend": "Coste API ({n}d)",
    "insights.creditsLeft": "Créditos restantes",
    "insights.credits": "Créditos usados ({n}d)",
    "insights.modelCalls": "Llamadas al modelo",
    "insights.services": "Servicios",
    "insights.tools": "Herramientas",
    "insights.viewAll": "Ver análisis",
    "stat.agents": "Agentes",
    "stat.agents.open": "Agentes — abrir la vista de agentes",
    "stat.channels": "Canales",
    "stat.scheduledTasks": "Tareas programadas",
    "stat.status": "Estado",
    "stat.status.live": "Activo",
    "stat.running": "{n} en ejecución",
    "stat.idle": " · {n} inactivos",
    "stat.active": "{n} activos",
    "stat.activeOf": "{n} de {total} activos",
    "stat.noConnectors": "sin conectores",
    "stat.enabled": "{n} habilitadas",
    "stat.noneEnabled": "ninguna habilitada",
    "stat.uptime": "tiempo activo {value}",
    "sys.title": "Control del sistema",
    "sys.desc.live":
      "El servidor de runtime de Olano está activo. Reiniciar recarga todos los agentes y canales; Detener los detiene todos.",
    "sys.desc.stopped":
      "El servidor de runtime de Olano está detenido. Iniciar arranca el servidor, sus agentes y canales.",
    "sys.start": "Iniciar",
    "sys.start.busy": "Iniciando…",
    "sys.stop": "Detener",
    "sys.stop.busy": "Deteniendo…",
    "sys.restart": "Reiniciar",
    "sys.restart.busy": "Reiniciando…",
    "toast.systemStarted": "Sistema iniciado",
    "toast.systemStopped": "Sistema detenido",
    "toast.systemRestarted": "Sistema reiniciado",
    "toast.systemStartFailed": "Error al iniciar el sistema: {err}",
    "toast.systemStopFailed": "Error al detener el sistema: {err}",
    "toast.systemRestartFailed": "Error al reiniciar el sistema: {err}",
    "err.unknown": "error desconocido",
    "toast.agentStarted": "{name} iniciado",
    "toast.agentStopped": "{name} detenido",
    "toast.agentRestarted": "{name} reiniciado",
    "toast.agentStartFailed": "No se pudo iniciar {name}: {err}",
    "toast.agentStopFailed": "No se pudo detener {name}: {err}",
    "toast.agentRestartFailed": "No se pudo reiniciar {name}: {err}",
    "agents.heading": "Agentes",
    "agents.collapse": "Contraer agentes",
    "agents.expand": "Expandir agentes",
    "agents.viewAll": "Ver todos",
    "agents.prevPage": "Página anterior",
    "agents.nextPage": "Página siguiente",
    "agents.empty.title": "Aún no hay agentes",
    "agents.empty.desc":
      "Crea tu primer agente para empezar: puede chatear, ejecutar herramientas y responder en Telegram, Slack, Discord o WhatsApp.",
    "agents.empty.create": "Crear agente",
    "activity.heading": "Actividad reciente",
    "activity.clear": "Limpiar",
    "activity.clear.title": "Limpiar la actividad reciente de esta vista",
    "activity.allLogs": "Todos los registros",
    "activity.empty": "Los registros aparecerán aquí en tiempo real.",
    "agent.noDescription": "Sin descripción.",
    "agent.chat": "Chat",
    "agent.edit": "Editar",
    "agent.restart": "Reiniciar",
    "agent.stop": "Detener",
    "agent.start": "Iniciar",
    "agent.close": "Cerrar",
    "agent.closeDetails": "Cerrar detalles",
    "agent.connectedChannels": "Canales conectados",
    "cap.subagents": "Subagentes",
    "cap.tools": "Herramientas",
    "cap.skills": "Habilidades",
    "cap.mcps": "MCP",
    "cap.channels": "Canales",
    "badge.subagent": "{n} subagente",
    "badge.subagents": "{n} subagentes",
    "badge.tool": "{n} herramienta",
    "badge.tools": "{n} herramientas",
    "badge.skill": "{n} habilidad",
    "badge.skills": "{n} habilidades",
    "badge.mcp": "{n} mcp",
    "badge.mcps": "{n} mcp",
    "badge.channel": "{n} canal",
    "badge.channels": "{n} canales",
    "status.running": "en ejecución",
    "status.error": "error",
    "status.stopped": "detenido",
    "status.starting": "iniciando",
    "status.stopping": "deteniendo",
    "status.paused": "pausado",
    "status.configured": "configurado",
    "credits.title": "Créditos de Olano",
    "credits.subtitle": "Paga la IA gestionada por Olano en este despliegue",
    "credits.staleSuffix": " · mostrando saldo en caché (nube inaccesible)",
    "credits.balanceBreakdown": "{included} incluidos + {purchased} comprados",
    "credits.left": "restantes",
    "credits.monthlyIncluded": "Incluido mensual",
    "credits.resetsSuffix": " · se reinicia el {reset}",
    "credits.usedLabel": "Créditos mensuales incluidos usados",
    "credits.usedOf": "{used} / {total} usados",
    "credits.purchasedTopups": "Recargas compradas (no caducan):",
    "credits.buyCredits": "Comprar créditos",
    "services.title": "Créditos de Olano por servicio",
    "services.lastDays": "Últimos {days} días",
    "services.staleSuffix": " · mostrando cifras en caché (nube inaccesible)",
    "services.total": "total",
    "disk.hint.usage":
      "Cuánto ocupa el sistema de archivos monitorizado, medido igual que `df`. Las marcas indican los umbrales de aviso y de limpieza automática.",
    "disk.hint.autoClean":
      "Cuando el uso supera el umbral de limpieza automática, el runtime ejecuta las acciones de limpieza habilitadas (por defecto solo las seguras: cachés y registros antiguos). Configura los umbrales y las acciones en Configuración → Monitor de disco y limpieza.",
    "disk.hint.cleanNow":
      "Ejecuta ahora mismo las acciones de limpieza habilitadas (docker prune puede tardar varios minutos). El runtime sigue funcionando mientras se ejecuta.",
    "disk.hint.lastCleanup": "Lo que recuperó la última limpieza, por acción.",
    "disk.hint.actions":
      "Qué acciones de limpieza están habilitadas y si pueden ejecutarse en este host. Las acciones agresivas (docker prune) son opcionales — actívalas en Configuración → Monitor de disco y limpieza.",
    "disk.hint.largest":
      "Dónde están los bytes dentro del home de Olano únicamente — según el escaneo diario de directorios (limitado; dos niveles de profundidad, más dos bajo cada agente). No incluye Docker ni el sistema operativo; para eso, mira «A dónde fue el espacio» más arriba. Haz clic en un directorio para ver su contenido.",
    "disk.hint.scanNow":
      "Escanea el home de olano ahora mismo en lugar de esperar al escaneo diario (se ejecuta en segundo plano, con prioridad baja y limitado). También vuelve a leer las cifras de Docker y del disco completo de arriba.",
    "disk.hint.breakdown":
      "Cada byte de espacio usado en este disco, agrupado: las imágenes y volúmenes de Docker sobre los que corre este despliegue, el home de Olano (agentes, transcripciones, memoria, habilidades), el archivo de intercambio y — como lo que queda — el sistema operativo y otros archivos del host. El home de Olano suele ser una porción pequeña; el resto es la plataforma en sí. Las cifras vienen del runtime de contenedores y del escaneo diario del home, así que son precisas aproximadamente al uno por ciento, no al byte.",
    "disk.hint.breakdownSystem":
      "Este grupo se mide como el espacio usado menos todo lo demás, porque el runtime corre dentro de un contenedor y no puede leer los directorios propios del host. Incluye el sistema operativo y el kernel, el motor de Docker, los registros del sistema, las cachés de paquetes y el checkout del despliegue.",
    "disk.breakdown.title": "A dónde fue el espacio · {used} usados de {total}",
    "disk.breakdown.reclaimable": "{bytes} recuperables",
    "disk.breakdown.free": "Libre",
    "disk.breakdown.notMeasured": "Sin medir:",
    "disk.expand": "Expandir detalles de disco",
    "disk.collapse": "Contraer detalles de disco",
    "disk.title": "Disco",
    "disk.unavailable": "No disponible: {err}",
    "disk.unavailableFallback": "no disponible",
    "disk.loading": "Cargando…",
    "disk.word.on": "activado",
    "disk.word.off": "desactivado",
    "disk.summary.collapsed": "{free} libres de {total} en {path} · limpieza automática {state}",
    "disk.cleaningSuffix": " · limpiando ahora…",
    "disk.summary.expanded": "{path} · {free} libres de {total} · comprobado cada {interval} min",
    "disk.envDisabledSuffix": " · deshabilitado por LIONCLAW_NO_DISK_MONITOR=1",
    "disk.autoClean.on": "Limpieza automática: activada",
    "disk.autoClean.off": "Limpieza automática: desactivada",
    "disk.clean.now": "Limpiar ahora",
    "disk.clean.busy": "Limpiando…",
    "disk.stat.used": "Usado",
    "disk.stat.free": "Libre",
    "disk.stat.lastCleanup": "Última limpieza",
    "disk.never": "nunca",
    "disk.warnPct": "aviso {pct}%",
    "disk.autoCleanPct": "limpieza automática {pct}%",
    "disk.legend": "aviso {warn}% · limpieza automática {cleanup}%",
    "disk.largestDirectories": "Directorios más grandes del home de Olano",
    "disk.scanSuffix": " · {bytes} escaneados {time}",
    "disk.partialSuffix": " (parcial)",
    "disk.scan.now": "Escanear ahora",
    "disk.scan.busy": "Escaneando…",
    "disk.scan.firstRunning": "Primer escaneo en curso…",
    "disk.scan.noneYet": "Aún no hay escaneo — se ejecuta a diario, o pulsa Escanear ahora.",
    "disk.dirRow.title": "{files} archivos — clic para {action}",
    "disk.action.collapse": "contraer",
    "disk.action.seeInside": "ver el contenido",
    "disk.filesCount": "{files} archivos",
    "disk.cleanupActions": "Acciones de limpieza",
    "disk.aggressive": "agresiva",
    "disk.lastCleanupHeading": "Última limpieza · {trigger} · {before}% → {after}%",
    "disk.reasonWrapped": "({reason})",
    "memory.hint.rss": "Resident Set Size (RSS) — la RAM física que el runtime está usando ahora mismo.",
    "memory.hint.pss":
      "Proportional Set Size (PSS) — como el RSS, pero la memoria compartida con otros programas se cuenta solo en proporción. La cifra más justa de lo que realmente cuesta este proceso.",
    "memory.hint.vms":
      "Virtual Memory Size (VMS) — el espacio de direcciones total que el proceso ha reservado, incluida la memoria que no está usando activamente. Normalmente mucho mayor que el uso real de RAM, y no es motivo de preocupación por sí solo.",
    "memory.hint.threads": "Número de hilos del sistema operativo que el runtime está ejecutando actualmente.",
    "memory.hint.uptime": "Cuánto tiempo lleva el runtime en funcionamiento desde su último (re)inicio.",
    "memory.hint.toolArgs":
      "Entradas en el registro de argumentos de herramientas — una caché limitada de los argumentos de llamadas a herramientas recientes.",
    "memory.hint.hookCtx": "Contextos de hook por conversación activos que se mantienen en memoria.",
    "memory.hint.offsets": "Posiciones de lectura del autoaprendizaje registradas en los logs de sesión.",
    "memory.hint.runners": "Runtimes de agentes actualmente cargados en memoria.",
    "memory.hint.embedModel":
      "Si el modelo de embeddings de texto está cargado en RAM (usado para Cortex / búsqueda de conocimiento).",
    "memory.hint.sampler":
      "Con qué frecuencia el monitor en segundo plano registra una lectura de memoria — “off” cuando el monitor está deshabilitado.",
    "memory.hint.bootRss":
      "RAM aproximada que añadió cada agente al arrancar, medida alrededor del inicio. Orientativo, no exacto.",
    "memory.hint.subprocesses":
      "RAM usada por programas auxiliares (navegador, conectores MCP, signal-cli) que se ejecutan como procesos separados, además de la memoria propia del runtime.",
    "memory.expand": "Expandir detalles de memoria",
    "memory.collapse": "Contraer detalles de memoria",
    "memory.title": "Memoria",
    "memory.unavailable": "No disponible: {err}",
    "memory.unavailableFallback": "no disponible",
    "memory.loading": "Cargando…",
    "memory.word.on": "activado",
    "memory.word.off": "desactivado",
    "memory.summary.monitorSuffix": " · monitor {state}",
    "memory.summary.source": "fuente: {source}",
    "memory.noDaemonSuffix": " · sin daemon",
    "memory.monitorToggleHint":
      "Alternar el muestreador de memoria en segundo plano + tracemalloc (desactivarlo libera la memoria propia del monitor)",
    "memory.monitor.on": "Monitor: activado",
    "memory.monitor.off": "Monitor: desactivado",
    "memory.stat.rss": "RSS",
    "memory.stat.pss": "Pss",
    "memory.stat.vmSize": "VmSize",
    "memory.stat.threads": "Hilos",
    "memory.stat.uptime": "Tiempo activo",
    "memory.term.toolArgs": "argumentos de herramienta",
    "memory.term.hookCtx": "contexto de hook",
    "memory.term.offsets": "desplazamientos",
    "memory.term.runners": "ejecutores",
    "memory.term.embedModel": "modelo de embeddings",
    "memory.term.sampler": "muestreador",
    "memory.embedModel.resident": "cargado",
    "memory.embedModel.no": "no",
    "memory.sampler.interval": "{n}s",
    "memory.tracemallocOn": " · tracemalloc activado",
    "memory.perAgentHeading": "Δ-RSS de arranque por agente (aprox.)",
    "memory.subprocessesHeading": "Subprocesos · +{mb} (PID separados)",
  },
};
